import React, { memo, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PriceDisplay } from './PriceDisplay';
import { ProductBadges } from './ProductBadges';
import TechieScoreBar from './TechieScoreBar';
import { getProductTitle, getTechieScore, getTechieScoreSummary } from '../../utils/dataFallbacks';

export const ModernProductCard = memo(function ModernProductCard({ product }) {
  const discount = useMemo(() => product.price_old 
    ? Math.round(((product.price_old - product.price) / product.price_old) * 100)
    : 0, [product.price, product.price_old]);
    
  // Generate content_rating using fallback utility
  const techieScore = useMemo(() => getTechieScore(product), [product]);
  const techieScoreSummary = useMemo(() => getTechieScoreSummary(product), [product]);

  return (
    <Link to={`/dp/${product.asin}`} className="group block">
      <div className="flex-shrink-0 w-full border border-slate-200 dark:border-[#1e2d4a] rounded-xl p-3 sm:p-4 bg-white dark:bg-[#0f1729] space-y-3 hover:shadow-xl dark:hover:shadow-2xl dark:hover:shadow-blue-900/20 transition-all duration-300 group-hover:border-blue-300 dark:group-hover:border-blue-500/50 group-hover:-translate-y-1" data-analytics-id="product-card">
        <div className="relative">
          {/* Product Image */}
          <div className="w-full h-40 sm:h-48 bg-white rounded-md flex items-center justify-center overflow-hidden">
            {product.image_url ? (
              <img 
                src={product.image_url} 
                alt={`${product.brand || 'Product'} ${getProductTitle(product)} - ${product.category || 'Tech Product'} with price tracking and deals`}
                className="w-auto h-auto max-w-full max-h-full object-contain transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            {!product.image_url && (
              <span className="text-gray-400 text-xs sm:text-sm">No image available</span>
            )}
            {product.image_url && (
              <span className="text-gray-400 text-xs sm:text-sm hidden items-center justify-center w-full h-full">No image available</span>
            )}
          </div>
          
          {/* Enhanced Dynamic Badges */}
          <div className="absolute top-2 left-2 z-10">
            <ProductBadges product={product} compact={true} />
          </div>
          
          {/* Discount Badge */}
          {discount > 0 && (
            <div className="absolute top-2 left-2 sm:top-4 sm:left-4">
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full animate-pulse">
                {discount}% OFF
              </span>
            </div>
          )}
          
          {/* Prime Badge */}
          {product.is_prime && (
            <div className="absolute top-2 right-2 sm:top-4 sm:right-4">
              <span className="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                Prime
              </span>
            </div>
          )}

          {/* Trust Badge */}
          <div className="absolute bottom-2 right-2">
            <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full border border-green-200">
              ✅ Verified
            </span>
          </div>
        </div>
        
        <div className="flex flex-col flex-grow min-h-0">
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-1 uppercase tracking-wide font-medium">
            {product.brand || 'Unknown Brand'}
          </p>
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 flex-grow mb-2 line-clamp-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
            {getProductTitle(product)}
          </h3>
          
          {/* TechieDay Score */}
          <div className="mb-3">
            <TechieScoreBar 
              score={techieScore} 
              compact={true} 
              showLabel={false} 
              summary={techieScoreSummary}
            />
          </div>
          
          {/* Price with Policy-Compliant Display */}
          <div className="mt-auto">
            <PriceDisplay
              price={product.price}
              priceOld={product.price_old}
              lastUpdated={product.updated_at}
              stockStatus={product.stock_status}
              asin={product.asin}
              size="small"
              showWarning={true}
            />
          </div>
        </div>
      </div>
    </Link>
  );
});
